// The one palette and type scale (ADR 0008). Every colour and text size on a screen comes from
// here; tokens.css mirrors the palette as CSS variables, and a unit test keeps the two equal.
// The colour check (tests/unit/design/palette-check.test.js) fails on a colour written anywhere
// else, so a new colour starts as an entry here, named by its role.

export const palette = Object.freeze({
  terracotta: '#C07A56',
  terracottaDark: '#A8654A',
  terracottaDeep: '#8F5540',
  caramelGold: '#E3BD90',
  caramelDark: '#D4A76A',
  caramelDeep: '#C09860',
  ivory: '#FAF7F2',
  warmGrey: '#F3EFEA',
  white: '#FFFFFF',
  espresso: '#2C1810',
  walnut: '#5C4A3D',
  driftwood: '#9C8B7A',
  border: '#E6DDD3',
  sageGreen: '#6B8F71',
  sageDark: '#527A58',
  warmAmber: '#D4943A',
  mutedBlue: '#6B87A8',
  errorRed: '#C4544A',
  japanAccent: '#D4544A',
  chinaAccent: '#C4463A',
  goldAccent: '#C9A06C',
  // The booking value charts. Chosen to stay apart for colour-blind readers; values unchanged.
  chartReceived: '#2F7A45',
  chartAwaiting: '#B8802A',
  // A full session on the calendar: a cool grey, so it never reads as cancelled (driftwood).
  sessionFull: '#9AA0A6',
  // The reader's own messages in a conversation.
  messageMine: '#F6E7DD',
  // Background colours for initials avatars, by palette name. Avatar picks white or espresso
  // initials per background so each meets 4.5:1 (espresso on mutedBlue, white on the rest).
  avatar: Object.freeze(['terracottaDeep', 'sageDark', 'walnut', 'mutedBlue']),
});

// Text sizes in pixels. Body text is never below 14; labels, badges and captions never below 12.
export const type = Object.freeze({
  caption: 12,
  label: 12,
  badge: 12,
  body: 14,
  lead: 16,
  h4: 18,
  h3: 20,
  h2: 28,
  h1: 40,
});

export const font = Object.freeze({
  display: "'Lora', serif",
  body: "'Plus Jakarta Sans', sans-serif",
  mono: "'JetBrains Mono', monospace",
});

// The only opacities a translucent palette colour may take.
export const OPACITY = Object.freeze({
  hover: 0.08, // a hovered or chosen item's background
  tint: 0.1, // a badge, notice or calendar block background
  selection: 0.25, // a tinted box's border, selected text, a selected block
  shadow: 0.1, // drop shadows
  scrim: 0.45, // behind a pop-up, or under text laid over a photo
});

// A palette colour at one of the named opacity steps: alpha('terracotta', 'tint').
export function alpha(name, step) {
  const hex = palette[name];
  if (typeof hex !== 'string') throw new Error(`alpha: "${name}" is not a palette colour`);
  if (!Object.prototype.hasOwnProperty.call(OPACITY, step)) {
    throw new Error(`alpha: "${step}" is not an opacity step (${Object.keys(OPACITY).join(', ')})`);
  }
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${OPACITY[step]})`;
}

// palette key → CSS variable name: sageGreen → --sage-green.
export function cssVar(name) {
  return '--' + name.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
}
